import { useMutation, useQuery } from "react-query";
import { ChargeSessionService } from "./charge-session.service";
import toast from "react-hot-toast";

// GET List
const useGetList = (page: number, query?: Record<string, any>, options?: { enabled?: boolean }) => {
  return useQuery(
    ["listSession", page, query],
    async () => {
      const { data } = await ChargeSessionService.getListChargeSession({
        page,
        ...query,
      });
      return data;
    },
    { 
      keepPreviousData: true,
      enabled: options?.enabled ?? true 
    },
  );
};

// See docs/evse-detail-fe-api-integration.md §9. For charger-utilization the response also
// carries `utilisation_rate` (a single summary number for the period) + `utilisation_rate_type`.
export type EvseStatisticsResponse = {
  ok: boolean;
  data?: Record<string, number>;
  utilisation_rate?: number | null;
  utilisation_rate_type?: "daily" | "weekly" | "monthly";
};

const useEvseStatistics = (params: {
  type: "number-of-sessions" | "charging-revenue" | "charger-utilization";
  evse_id?: string;
  from_date?: string;
  to_date?: string;
  rating_type?: "daily" | "weekly" | "monthly";
}) => {
  return useQuery<EvseStatisticsResponse>(
    ["evseStatistics", params],
    async () => {
      const { data } = await ChargeSessionService.getEvseStatistics(
        params as {
          type: "number-of-sessions" | "charging-revenue" | "charger-utilization";
          evse_id: string;
          from_date?: string;
          to_date?: string;
        },
      );
      return data;
    },
    {
      keepPreviousData: true,
      enabled: Boolean(params.evse_id) && Boolean(params.from_date) && Boolean(params.to_date),
    },
  );
};

const useStatisticsByCharger = (
  params: {
    page?: number;
    per_page?: number;
    from_date?: string;
    to_date?: string;
    charger_type?: "ac" | "dc" | "unknown" | "all";
    status?: "active" | "inactive" | "all";
    type?: "by-charger";
  } = {},
) => {
  return useQuery(
    ["statisticsByCharger", params],
    async () => {
      const { data } = await ChargeSessionService.getStatisticsByCharger(params);
      return data;
    },
    {
      keepPreviousData: true,
    },
  );
};


const useStatisticsByLocation = (
  params: {
    page?: number;
    per_page?: number;
    from_date?: string;
    to_date?: string;
    CPO?: string;
    SeedingPartner?: string;
    type?: "location-performance";
  } = {},
) => {
  return useQuery(
    ["statisticsByLocation", params],
    async () => {
      const { data } = await ChargeSessionService.getStatisticsByLocation(params);
      return data;
    },
    {
      keepPreviousData: true,
    },
  );
};

const useStatisticsChargingSessionPerformance = (
  params: {
    page?: number;
    per_page?: number;
    session_id?: string;
    status?: string;
    from_date?: string;
    to_date?: string;
    CPO?: string;
    SeedingPartner?: string;
    type?: "charging-session-performance";
  } = {},
) => {
  return useQuery(
    ["statisticsChargingSessionPerformance", params],
    async () => {
      const { data } = await ChargeSessionService.getStatisticsChargingSessionPerformance(
        params,
      );
      return data;
    },
    {
      keepPreviousData: true,
    },
  );
};

// GET Detail — unified structured resource (charging session / reservation / idling views)
const useGetDetail = (id: string) => {
  return useQuery(["getDetail", id], async () => {
    const { data } = await ChargeSessionService.getDetailChargeSession(id);
    return (data?.data ?? data) as ChargeSessionDetailResource;
  });
};

const useGetSettleEnergy = (id: string) => {
  return useQuery(["getSettleEnergy", id], async () => {
    if (id) {
      const { data } =
        await ChargeSessionService.getSettleEnergyChargeSession(id);
      return data;
    }

    return [];
  });
};

// const useSettleEnergyApply = (id: string) => {
//   return useQuery(["getSettleEnergy", id], async () => {
//     const { data } = await ChargeSessionService.applySettleEnergyChargeSession(id);
//     return data;
//   });
// };

const useSettleEnergyApply = (cb?: (data: any) => void) => {
  return useMutation(
    async (payload: any) => {
      const data =
        await ChargeSessionService.applySettleEnergyChargeSession(payload);
      return data;
    },
    {
      onSuccess: (data) => {
        cb?.(data);
        if (data.ok) {
          toast.success("Invalidate successfully!");
        } else {
          toast.error(data?.msg);
        }
      },
      onError(error, _variables, _context) {
        alert(error);
      },
    },
  );
};

const useFlushSession = (_cb?: (data: any) => void) => {
  return useMutation(
    async (payload: any) => {
      const data =
        await ChargeSessionService.flushSession(payload);
      return data;
    }
  );
};


export {
  useGetList,
  useGetDetail,
  useGetSettleEnergy,
  useSettleEnergyApply,
  useFlushSession,
  useStatisticsByLocation,
  useStatisticsByCharger,
  useStatisticsChargingSessionPerformance,
  useEvseStatistics,
};
